import { observer } from 'mobx-react';
import { Outlet, useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { AdvertiserModel } from '../../../../modules/models/advertiser.model';
import routes from '../../../../modules/routes/index.routes';
import { useAdvertiserActions } from '../../../../modules/hooks/actions/advertiser.actions';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useAdvertiserHeaders } from '../../../../modules/hooks/theaders/advertisers.headers';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IMFPStore } from '../../../../modules/stores';
import {
	Entities,
	IPerformanceOptionsFilterType,
	IPerformanceOptionsSortBy,
	PerformancesColumns,
	useReportsList,
} from '../../../../modules/hooks/apis/use-reports-list';
import { PrimaryLink } from '@monorepo/base/src/components/buttons/buttons';
import { useMetricsGet } from '../../../../modules/hooks/tools/use-metrics-get';
import { availablePerformanceToMetrics } from '../../../../modules/models/performance.model';
import { useMetrics } from '../../../../modules/hooks/tools/use-metrics';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { ManifestoChart } from '../../../../modules/components/chart/manifesto-chart';
import styles from './../../../../styles/pages.module.scss';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { useStateBetweenPages } from '@monorepo/tools/src/lib/hooks/tools/use-state-between-pages';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { Helmet } from 'react-helmet-async';
import { isFetchFromBackend } from '../../../../modules/helpers/reports.helpers';
import { PaginationState } from '@tanstack/react-table';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';

export const AccountAdvertisers = observer(() => {
	const { advertiserStore, accountStore } = useStores<IMFPStore>();
	const metricsStore = advertiserStore.getMetricsStore();
	const reportsStore = advertiserStore.getReportsStore();
	const reportData = reportsStore.getData();
	const { accountId } = useParams();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const stateBetweenPages = useStateBetweenPages(() => Boolean(reportData?.getData()) && reportData?.getData().length !== 0);
	const accountName = accountStore.getCrud().getData().getName();
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();

	if (!accountId) {
		return null;
	}

	const { reportsError, fetchReports } = useReportsList<AdvertiserModel>(
		reportsStore,
		{
			entity: Entities.Advertisers,
			filters: [
				{
					column: PerformancesColumns.AccountId,
					filterType: IPerformanceOptionsFilterType.Include,
					value: [accountId],
				},
			],
			groupBys: [PerformancesColumns.AdvertiserId],
			sortBys: defaultSortBy?.map(sort => {
				return {
					column: sort.id as IPerformanceOptionsSortBy,
					order: sort.desc ? 'desc' : 'asc',
				};
			}),
		},
		stateBetweenPages,
		advertiserStore.getSegments()
	);

	const { metricsError, fetchMetrics } = useMetricsGet(
		metricsStore,
		{
			entity: Entities.Chart,
			groupBys: [PerformancesColumns.Date],
			sortBys: defaultSortBy?.map(sort => {
				return {
					column: sort.id as IPerformanceOptionsSortBy,
					order: sort.desc ? 'desc' : 'asc',
				};
			}),
			filters: [
				{
					column: PerformancesColumns.AccountId,
					filterType: IPerformanceOptionsFilterType.Include,
					value: [accountId],
				},
			],
		},
		stateBetweenPages
	);

	const { columns } = useAdvertiserHeaders({
		onEdit: accountId ? (id: id) => routes.accounts.editAdvertiser(accountId, id) : undefined,
		summary: reportData?.getSummary(),
	});
	const { SelectedActions, FiltersActions, SegmentActions } = useAdvertiserActions({
		currentSegments: advertiserStore.getSegments(),
		onEdit: accountId ? (id: id) => routes.accounts.editAdvertiser(accountId, id) : undefined,
	});

	const { metrics, xLabels } = useMetrics(metricsStore.getData());

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>{accountName || 'Account'} - Advertisers</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }}>
				<PagebarTitle>Advertisers</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
					<PrimaryLink
						state={{ isBlockRequest: true } as BetweenPagesStateProps}
						icon={'plus'}
						to={accountId ? routes.accounts.createAdvertiser(accountId) : ''}
						width={'148px'}>
						New Advertiser
					</PrimaryLink>
				</PagebarActions>
			</Pagebar>
			<ManifestoChart
				isLoading={metricsStore.getIsLoading()}
				metrics={metrics}
				labels={xLabels}
				legendOptions={Object.keys(availablePerformanceToMetrics)}
				isError={!!metricsError && !isAbortError(metricsError)}
			/>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				classes={{ wrapper: styles.padding }}
				data={reportData?.getData() || []}
				isLoading={reportsStore.getIsLoading()}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'advertisers_account_table')] }}
				isWithPerformance={true}
				isLoadingPerformance={reportsStore.getIsLoading()}
				defaultSortBy={defaultSortBy}
				fetchReports={fetchReports}
				fetchMetrics={fetchMetrics}
				onPageChange={(state: PaginationState) => {
					if (isFetchFromBackend(reportData, state) && !reportsStore.getIsLoading()) {
						fetchReports({ appendData: true, resetOffset: false });
					}
				}}
				isError={!!reportsError && !isAbortError(reportsError)}>
				{({ sortedRows }) => {
					advertiserStore.setSortedRows(sortedRows);

					return {
						SelectedActions,
						SegmentActions,
						FiltersActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
